import { Button, IconButton } from 'rsuite';
import { MdClose, MdLocationOn, MdDateRange, MdShoppingCart } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { getItemEffectivePrice, formatPrice, formatDateRange } from '../../services/utilService';

const CampCart = ({ cart, onRemoveItem }) => {
  const navigate = useNavigate();

  const handleRemoveItem = (itemToRemove) => {
    if (onRemoveItem) {
      onRemoveItem(itemToRemove);
    }
  };

  if (!cart) return null;

  const cartTotal = cart.reduce((total, item) => total + getItemEffectivePrice(item), 0);
  const fullSubtotal = cart.reduce((total, item) => {
    const seasonPrice = Number(item?.week?.season?.price);
    if (!isNaN(seasonPrice) && seasonPrice > 0) return total + seasonPrice;
    const fallback = typeof item.price === 'string'
      ? Number(item.price.replace(/[$,]/g, ''))
      : Number(item.price) || 0;
    return total + fallback;
  }, 0);
  const seasonSaving = Math.max(0, fullSubtotal - cartTotal);

  return (
    <div className="h-full flex flex-col bg-white" style={{ padding: '20px 20px 0' }}>

      {/* Scrollable cart items */}
      <div className="flex-1 overflow-y-auto">
        {cart.length === 0 ? (
          /* No "Browse camps" CTA here — this panel only renders on the booking
             page, where the user is already browsing */
          <div className="text-center py-10 px-2">
            <MdShoppingCart size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="font-medium text-gray-700 mb-1">Your cart is empty</p>
            <p className="text-sm text-gray-500 mb-4">Camps you add will show up here.</p>
          </div>
        ) : (
          cart.map((item) =>
            <div key={item.week._id + item.kid._id} className="mb-3">
              {/* Cart Item Card */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 relative">

                {/* Remove button */}
                <IconButton
                  icon={<MdClose />}
                  size="xs"
                  appearance="subtle"
                  aria-label={`Remove ${item.week.theme} for ${item.kid.firstName}`}
                  className="text-gray-400 hover:text-red-500"
                  onClick={() => handleRemoveItem(item)}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    zIndex: 1
                  }}
                />

                {/* Camp first (bold), child second (muted) */}
                <div className="mb-2 pr-6">
                  <p className="text-sm font-semibold text-gray-900 m-0 leading-tight">
                    {item.week.theme}
                  </p>
                  <p className="text-sm text-gray-500 m-0 mt-0.5">
                    for {item.kid.firstName}
                  </p>
                </div>

                {/* Location */}
                <div className="flex items-start mb-2">
                  <MdLocationOn className="text-gray-500 mr-2 mt-0.5 flex-shrink-0" size={14} />
                  <div className="text-sm text-gray-600 leading-tight">
                    <div className="font-medium">{item.week.location.name}</div>
                  </div>
                </div>

                {/* Dates */}
                <div className="flex items-center mb-2">
                  <MdDateRange className="text-gray-500 mr-2" size={14} />
                  <span className="text-sm text-gray-600">
                    {formatDateRange(item.week.startDate, item.week.endDate)}
                  </span>
                </div>

                {/* Day Option and Price */}
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-sm text-gray-600">
                    {item.dayOption}
                  </span>
                  <span className="font-semibold text-gray-800">
                    ${formatPrice(getItemEffectivePrice(item))}
                  </span>
                </div>

              </div>

            </div>
          )
        )}
      </div>

      {/* Sticky footer: count · total, savings, full-width CTA */}
      {cart.length > 0 && (
        <div className="border-t border-gray-200 pt-3 pb-4 bg-white">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">
              {cart.length} item{cart.length !== 1 ? 's' : ''}
            </span>
            <span className="text-lg font-bold text-gray-900">
              ${formatPrice(cartTotal)}<span className="currency-suffix">SGD</span>
            </span>
          </div>
          {seasonSaving > 0 && (
            <div className="flex justify-between items-center mt-0.5">
              <span className="text-xs text-green-700">You save</span>
              <span className="text-xs text-green-700 font-medium">
                ${formatPrice(seasonSaving)}
              </span>
            </div>
          )}
          <Button
            block
            appearance="primary"
            size="lg"
            className="mt-3"
            onClick={() => navigate('/review')}
          >
            Review Cart
          </Button>
        </div>
      )}

    </div>
  )
}

export default CampCart;
